import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./battleroulete.css";
import BBPattern from "../../assets/images/BBpattern.png";
import SpinBackground from "../../assets/images/spinBackground.png";
import { ReactComponent as Swords } from "../../assets/icons/swords.svg";
import BackdropLoader from "../BackdropLoader/BackdropLoader";
import { useSelector } from "react-redux";
import Crazy from "../../assets/icons/crazy.svg";
import {ReactComponent as CrazyZap} from "../../assets/icons/crazy_zap.svg";


const ITEM_HEIGHT = 100;
const GAP_BETWEEN_ITEMS = 35;
const TOTAL_ITEM_HEIGHT = ITEM_HEIGHT + GAP_BETWEEN_ITEMS;
const CONTAINER_HEIGHT = 300;
const VIEWPORT_CENTER = CONTAINER_HEIGHT / 2;

const MIN_SCROLL_ITEMS = 40;
const MIN_LIST_LENGTH = 60;

const SHADOW_COLORS = [
  "rgba(255, 215, 0, 0.8)",
  "rgba(255, 0, 255, 0.6)",
  "rgba(0, 191, 255, 0.6)",
  "rgba(255, 105, 180, 0.7)",
  "rgba(144, 238, 144, 0.7)",
];

const generateId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).substring(2)}`;

const randomPick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const RouletteProcessedData = (products, productId) => {
  try {
    if (!products || products.length === 0) {
      console.error("❌ RouletteProcessedData: no products");
      return { list: [], index: 0 };
    }

    const prizes = products.map((item) => {
      const imageUrl = item?.image?.startsWith("http")
        ? item.image
        : `${process.env.REACT_APP_IMAGE_URI || ""}/public/${item?.image}`;

      return {
        ...item,
        image: imageUrl,
        text: item?.name,
        user_id: item?.id,
        price: item?.value || 0,
        name: item?.name || "Unknown Item",
      };
    });

    const winnerIndex = Math.max(MIN_SCROLL_ITEMS, prizes.length * 5);
    const desiredLength = Math.max(MIN_LIST_LENGTH, winnerIndex + 15);

    const prizeList = Array.from({ length: desiredLength }, () => ({
      ...randomPick(prizes),
      id: generateId(),
    }));

    const winningProduct = products.find((item) => item.id === productId);
    let prizeIndex = winnerIndex;

    if (winningProduct) {
      const winningImageUrl = winningProduct.image?.startsWith("http")
        ? winningProduct.image
        : `${process.env.REACT_APP_IMAGE_URI || ""}/public/${winningProduct.image}`;

      const winningPrizeItem = {
        ...winningProduct,
        image: winningImageUrl,
        text: winningProduct.name,
        user_id: winningProduct.id,
        price: winningProduct.value || 0,
        name: winningProduct.name || "Unknown Item",
        id: generateId(),
      };

      while (prizeList.length <= prizeIndex) {
        prizeList.push({ ...randomPick(prizes), id: generateId() });
      }

      prizeList[prizeIndex] = winningPrizeItem;

      const tailNeeded = prizeIndex + 10;
      while (prizeList.length < tailNeeded) {
        prizeList.push({ ...randomPick(prizes), id: generateId() });
      }
    } else {
      console.error(`❌ Product ${productId} not found in products array`, {
        productId,
        availableProducts: products.map((p) => ({ id: p.id, name: p.name })),
      });
      prizeIndex = Math.floor(prizeList.length / 2);
    }

    return { list: prizeList, index: prizeIndex };
  } catch (error) {
    console.error("❌ RouletteProcessedData error:", error);
    return { list: [], index: 0 };
  }
};
const useSharedAudio = (rollSoundUrl, winSoundUrl) => {
  const audioCtxRef        = useRef(null);
  const gainRef            = useRef(null);
  const rollBufferRef      = useRef(null);
  const winBufferRef       = useRef(null);
  const rawRollRef         = useRef(null);
  const rawWinRef          = useRef(null);
  const rollSourceRef      = useRef(null);
  const rollTimerRef       = useRef(null);
  const unlockedRef        = useRef(false);

  // ── NEW: store fetch promises so callers can await them ──────────────────
  const rollFetchPromise   = useRef(null);
  const winFetchPromise    = useRef(null);

  useEffect(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;

    const ctx = new AC();
    audioCtxRef.current = ctx;

    const gain = ctx.createGain();
    gain.gain.value = 1.0;
    gain.connect(ctx.destination);
    gainRef.current = gain;

    // Returns a Promise that resolves once the raw ArrayBuffer is fetched
    // (and optionally decoded if the context is already running).
    const loadRaw = (url, rawRef, bufRef) => {
      const promise = (async () => {
        try {
          const res = await fetch(url);
          rawRef.current = await res.arrayBuffer();
          // Opportunistically decode if context is already unlocked
          if (ctx.state === "running") {
            try {
              bufRef.current = await ctx.decodeAudioData(rawRef.current.slice(0));
            } catch (_) {}
          }
        } catch (e) {
          console.error("Audio fetch failed:", url, e);
          // Resolve anyway — callers handle the missing buffer gracefully
        }
      })();
      return promise;
    };

    if (rollSoundUrl) rollFetchPromise.current = loadRaw(rollSoundUrl, rawRollRef, rollBufferRef);
    if (winSoundUrl)  winFetchPromise.current  = loadRaw(winSoundUrl,  rawWinRef,  winBufferRef);

    return () => {
      try { ctx.close(); } catch (_) {}
      audioCtxRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── User-gesture unlock (unchanged) ─────────────────────────────────────
  useEffect(() => {
    const unlock = async () => {
      if (unlockedRef.current) return;
      const ctx = audioCtxRef.current;
      if (!ctx) return;

      try {
        if (ctx.state === "suspended") await ctx.resume();
      } catch (e) {
        console.error("AudioContext resume failed", e);
        return;
      }

      unlockedRef.current = true;

      const tryDecode = async (rawRef, bufRef) => {
        if (!bufRef.current && rawRef.current) {
          try {
            bufRef.current = await ctx.decodeAudioData(rawRef.current.slice(0));
          } catch (e) {
            console.error("Decode failed on unlock", e);
          }
        }
      };

      await tryDecode(rawRollRef, rollBufferRef);
      await tryDecode(rawWinRef,  winBufferRef);
    };

    const events = ["click", "touchstart", "touchend", "mousedown", "keydown"];
    events.forEach((ev) => window.addEventListener(ev, unlock, { once: false }));
    return () => events.forEach((ev) => window.removeEventListener(ev, unlock));
  }, []);

  // ── ensureReady (unchanged) ──────────────────────────────────────────────
  const ensureReady = useCallback(async (rawRef, bufRef) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return false;

    if (ctx.state === "suspended") {
      try { await ctx.resume(); } catch (e) { return false; }
    }

    if (!bufRef.current && rawRef.current) {
      try {
        bufRef.current = await ctx.decodeAudioData(rawRef.current.slice(0));
      } catch (e) {
        console.error("Last-chance decode failed", e);
        return false;
      }
    }

    return !!bufRef.current;
  }, []);

  const stopRoll = useCallback(() => {
    if (rollTimerRef.current) {
      clearTimeout(rollTimerRef.current);
      rollTimerRef.current = null;
    }
    if (rollSourceRef.current) {
      try { rollSourceRef.current.stop(); } catch (_) {}
      rollSourceRef.current = null;
    }
    if (gainRef.current) gainRef.current.gain.value = 1.0;
  }, []);

  // ── FIXED playRoll: await the fetch promise first ────────────────────────
  const playRoll = useCallback(async (durationSec) => {
    // Wait for the raw ArrayBuffer to arrive (resolves instantly on 2nd+ box)
    if (rollFetchPromise.current) await rollFetchPromise.current;

    const ready = await ensureReady(rawRollRef, rollBufferRef);
    if (!ready) return;

    stopRoll();

    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const source = ctx.createBufferSource();
    source.buffer = rollBufferRef.current;
    source.loop = true;
    source.playbackRate.value = 0.9;
    gainRef.current.gain.value = 1.0;
    source.connect(gainRef.current);
    source.start(0);
    rollSourceRef.current = source;

    rollTimerRef.current = setTimeout(stopRoll, durationSec * 1000);
  }, [ensureReady, stopRoll]);

  // ── FIXED playWin: await the fetch promise first ─────────────────────────
  const playWin = useCallback(async () => {
    stopRoll();

    if (winFetchPromise.current) await winFetchPromise.current;

    const ready = await ensureReady(rawWinRef, winBufferRef);
    if (!ready) return;

    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const source = ctx.createBufferSource();
    source.buffer = winBufferRef.current;
    source.loop = false;
    gainRef.current.gain.value = 0.6;
    source.connect(gainRef.current);
    source.start(0);

    source.onended = () => {
      if (gainRef.current) gainRef.current.gain.value = 1.0;
    };
  }, [ensureReady, stopRoll]);

  return { playRoll, stopRoll, playWin };
};


const SingleRoulette = ({
  data,
  start,
  onPrizeDefined,
  playerKey,
  playRoll,
  stopRoll,
  playWin,
}) => {
  const [randomValue, setRandomValue] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const [winningIndex, setWinningIndex] = useState(null);
  const [centerIndex, setCenterIndex] = useState(null);
  const [boxImages, setBoxImages] = useState([]);
  const [transitionDuration, setTransitionDuration] = useState("none");
  const [initialized, setInitialized] = useState(false);

  const itemBoxRef        = useRef(null);
  const containerRef      = useRef(null);
  const gridRef           = useRef(null);
  const timeoutRef        = useRef(null);
  const animationFrameRef = useRef(null);
  const isRollingRef      = useRef(false);

  useEffect(() => {
    if (!data?.list?.length) {
      console.error(`❌ Invalid data for ${playerKey}`);
      return;
    }

    setBoxImages(data.list);

    const timer = setTimeout(() => {
      const randomStartIndex = Math.floor(Math.random() * 5) + 2;
      const initialOffset =
        -(randomStartIndex * TOTAL_ITEM_HEIGHT) +
        VIEWPORT_CENTER -
        ITEM_HEIGHT / 2;

      setRandomValue(initialOffset);
      setInitialized(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [data, playerKey]);

  const updateCenterItem = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const computedTransform = window.getComputedStyle(grid).transform;
    let currentY = 0;

    if (computedTransform && computedTransform !== "none") {
      const match = computedTransform.match(/matrix.*\((.+)\)/);
      if (match) {
        const values = match[1].split(",").map(Number);
        currentY = values.length === 6 ? values[5] : values[13] || 0;
      }
    }

    const offset = -currentY + VIEWPORT_CENTER - ITEM_HEIGHT / 2;
    const idx = Math.round(offset / TOTAL_ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(idx, (data?.list?.length || 1) - 1));

    setCenterIndex((prev) => (prev !== clamped ? clamped : prev));
  }, [data?.list?.length]);

  useEffect(() => {
    if (!isRolling) return;

    const animate = () => {
      updateCenterItem();
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isRolling, updateCenterItem]);

  const rollRandom = useCallback(() => {
    if (isRollingRef.current) {
      console.warn(`⚠️ ${playerKey} already rolling, ignoring duplicate start`);
      return;
    }

    setIsRolling(true);
    isRollingRef.current = true;
    playRoll(6);

    const targetIndex =
      data.index >= 0 ? data.index : Math.floor(boxImages.length / 2);
    setWinningIndex(targetIndex);

    if (itemBoxRef.current) itemBoxRef.current.classList.remove("br-highlight");

    setTransitionDuration("none");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const targetOffset =
          -(targetIndex * TOTAL_ITEM_HEIGHT) +
          VIEWPORT_CENTER -
          ITEM_HEIGHT / 2;

        setTransitionDuration("transform 5s cubic-bezier(0.05, 0.5, 0.15, 1)");
        setRandomValue(targetOffset);

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
          setIsRolling(false);
          isRollingRef.current = false;
          playWin();
          setCenterIndex(targetIndex);
          if (itemBoxRef.current) itemBoxRef.current.classList.add("br-highlight");
          if (onPrizeDefined) onPrizeDefined();
        }, 5100);
      });
    });
  }, [data, boxImages.length, playerKey, playRoll, playWin, onPrizeDefined]);

  useEffect(() => {
    if (start && !isRollingRef.current && boxImages.length > 0 && initialized) {
      rollRandom();
    }
  }, [start, initialized, boxImages.length, rollRandom]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      stopRoll();
    };
  }, [stopRoll]);

  const visibleRange = useMemo(() => {
    if (centerIndex == null) return { start: 0, end: 20 };
    return {
      start: Math.max(0, centerIndex - 5),
      end: Math.min(boxImages.length - 1, centerIndex + 5),
    };
  }, [centerIndex, boxImages.length]);

  if (!boxImages.length) {
    return <div className="br-loading-text">Loading {playerKey}...</div>;
  }

  return (
    <div className="br-roulette-container">
      <div className="br-box-wrapper">
        <div className="br-box-grid">
          <div className="br-box-container" ref={containerRef}>
            <div
              ref={gridRef}
              className="br-item-grid"
              style={{
                transform: `translate3d(0, ${randomValue}px, 0)`,
                transition: randomValue !== 0 ? transitionDuration : "none",
                willChange: isRolling ? "transform" : "auto",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
              }}
            >
              {boxImages.map((itemData, index) => {
                const isCenter   = centerIndex === index;
                const glowColor  = SHADOW_COLORS[index % SHADOW_COLORS.length];
                const isWinning  = index === winningIndex;
                const isNearViewport =
                  index >= visibleRange.start && index <= visibleRange.end;

                return (
                  <div
                    className="br-item-box"
                    key={`${playerKey}-${index}`}
                    ref={isWinning ? itemBoxRef : null}
                  >
                    <div className="br-item-box-content">
                      <div
                        className="br-roll-item"
                      >
                        {/* <div className="br-item-win-animation" />
                        {isNearViewport && (
                          <div
                            className="br-rarity-shadow"
                            style={{ background: glowColor }}
                          />
                        )} */}
                        <div   className="br-item-div">
                          <img
                          className="br-item-image"
                            src={itemData.image}
                            alt="Item"
                            loading={isNearViewport ? "eager" : "lazy"}
                            onError={(e) => {
                              console.error(`❌ Image failed to load: ${itemData.image}`);
                              e.target.src =
                                "https://via.placeholder.com/100?text=No+Image";
                            }}
                          />
                           {isWinning && !isRolling && (
                            <div className="br-price-tag">
                              <div className="br-price-value">
                                ${Number(itemData.price).toFixed(2)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BattleRoulete = ({
  products,
  boxCount,
  player1,
  player2,
  rollSound,
  winSound,
  battleDetail,
  onRoundComplete,
}) => {
  const [start, setStart] = useState(false);
  const [key, setKey] = useState(0);
  const [playerOne, setPlayerOne] = useState(null);
  const [playerTwo, setPlayerTwo] = useState(null);
  const [loading, setLoading] = useState(true);
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  const isFirstLoadRef = useRef(true);
  const reelsDoneRef   = useRef(0);

  const { playRoll, stopRoll, playWin } = useSharedAudio(rollSound, winSound);

  const animatingBox    = battleDetail?.box_detail?.animating_box;
  const currentBox      = battleDetail?.box_detail?.current_box;
  const displayBox      =
    animatingBox && animatingBox > 0
      ? animatingBox
      : currentBox > 0
        ? currentBox
        : 1;
  const displayBoxIndex = displayBox - 1;
  const totalBoxes      =
    battleDetail?.box_count || battleDetail?.box_detail?.total_box || 1;
  const currentBoxData  =
    battleDetail?.boxes?.[displayBoxIndex]?.box ||
    battleDetail?.box_detail?.box;
  const boxPrice = currentBoxData?.box_price; // eslint-disable-line no-unused-vars

  const handlePrizeDefined = useCallback(() => {
    reelsDoneRef.current += 1;
    if (reelsDoneRef.current >= 2 && onRoundComplete) {
      onRoundComplete();
    }
  }, [onRoundComplete]);

  const handlePlayerData = useCallback(() => {
    try {
      if (!products || products.length === 0) {
        console.error("❌ No products available");
        return;
      }

      if (!player1?.user_product?.id) {
        console.error("❌ Player 1 product missing:", player1);
        return;
      }

      if (!player2?.user_product?.id) {
        console.error("❌ Player 2 product missing:", player2);
        return;
      }

      if (isFirstLoadRef.current) setLoading(true);

      setStart(false);
      reelsDoneRef.current = 0;

      const data1 = RouletteProcessedData(products, player1.user_product.id);
      const data2 = RouletteProcessedData(products, player2.user_product.id);
      setPlayerOne(data1);
      setPlayerTwo(data2);
      setLoading(false);

      isFirstLoadRef.current = false;
      setKey((prev) => prev + 1);

      setTimeout(() => setStart(true), 300);
    } catch (e) {
      console.error("❌ Error processing roulette data:", e);
      setLoading(false);
      isFirstLoadRef.current = false;
    }
  }, [products, player1?.user_product?.id, player2?.user_product?.id]);

  useEffect(() => {
    handlePlayerData();
    return () => setStart(false);
  }, [handlePlayerData]);

  if (loading || !playerOne || !playerTwo) {
    return (
      <div className="br-loading-container">
        <div className="br-loading-text">
          <BackdropLoader show={true} />
        </div>
      </div>
    );
  }

  const backgroundStyle =
    battleDetail?.mode === "crazy"
      ? {
          backgroundImage : `url(${Crazy}), url(${SpinBackground})`,
          backgroundSize: "contain, cover",
          backgroundPosition: "center, center",
          backgroundRepeat: "no-repeat, no-repeat",
        }
      : {
        backgroundImage: `url(${SpinBackground})`,
        backgroundSize: "cover, auto",
          backgroundPosition: "center, center",
          backgroundRepeat: "no-repeat, repeat",
        };

  return (
    <div className="br-layout-wrapper container" style={backgroundStyle}>
      <div style={{ textAlign: "center" }}>
        <div
          style={{ color: isDarkMode ? "white" : "black" }}
          className="battle-type-text"
        >
          Round {displayBox}/{totalBoxes}
        </div>
      </div>
      <div className="br-main-wrapper">
          <div className="right-indicator" >
          <svg  width="18" height="27"  viewBox="0 0 12 20" style={{display: "block"}}>
            <path d="M12 10 L0 0 L0 20 Z" fill="#FFCB21" />
          </svg>
        </div>
        <div className="br-center-wrapper">
          <SingleRoulette
            key={`p1-${key}`}
            data={playerOne}
            start={start}
            onPrizeDefined={handlePrizeDefined}
            playerKey={`p1-${key}`}
            playRoll={playRoll}
            stopRoll={stopRoll}
            playWin={playWin}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {battleDetail?.mode === "crazy"? <CrazyZap  style={{ width: "40px", height: "40px" }}/>:
            <Swords
              className="sword-icon-color"
            style={{ width: "40px", height: "40px" }}
            />}
          </div>
          <SingleRoulette
            key={`p2-${key}`}
            data={playerTwo}
            start={start}
            onPrizeDefined={handlePrizeDefined}
            playerKey={`p2-${key}`}
            playRoll={playRoll}
            stopRoll={stopRoll}
            playWin={playWin}
          />
        </div>
          <div className="left-indicator">
          <svg width="18" height="27" viewBox="0 0 12 20" style={{display: "block" }}>
            <path d="M0 10 L12 0 L12 20 Z" fill="#FFCB21" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default BattleRoulete;